
import { Edit, Mail, KeyRound, Lock, Users, Copy, ClipboardCopy, Settings, Eye, Sliders, UserCheck, Link as LinkIcon } from "lucide-react";
import {
  TableColumn,
  TableAction,
  ActionHandler,
} from "@/components/section/EntityTableSection";
import { User } from "./api";
import { getRoleLabelByValue } from "./constants";
import { UserAction } from "./hooks/useUserActions";
import RelativeTime from "@/components/ui/RelativeTime";
import TableText from "@/components/ui/TableText";
import { getUserProfileUrl } from "@/config/env";

export const createUserColumns = (): TableColumn<User>[] => [
  {
    key: "id",
    title: "ID",
    variant: "id",
  },
  {
    key: "email",
    title: "EMAIL",
    render: (user) => (
      <TableText variant="primary">
        {user.email}
      </TableText>
    ),
  },

  {
    key: "name",
    title: "NAME",
    render: (user) => {
      const fullName = `${user.first_name} ${user.last_name}`;
      return (
        <TableText>
          {fullName}
        </TableText>
      );
    },
  },
  // {
  //   key: "title",
  //   title: "Title",
  //   width: "1.5",
  //   render: (user) => {
  //     const title = user.title || user.profile?.title?.title || user.profile?.occupation || 'N/A';
  //     return (
  //       <TableText>
  //         {title}
  //       </TableText>
  //     );
  //   },
  // },
  {
    key: "roles",
    title: "ROLES",
    render: (user) => {
      const rolesText = user.roles?.length > 0 
        ? user.roles.map(role => getRoleLabelByValue(role)).join(', ')
        : 'No Roles';
      return (
        <TableText>
          {rolesText}
        </TableText>
      );
    },
  },
  {
    key: "organization",
    title: "ORGANIZATION",
    render: (user) => {
      const orgName = user.organization?.name || 'N/A';
      return (
        <TableText>
          {orgName}
        </TableText>
      );
    },
  },
  {
    key: "created",
    title: "CREATED",
    render: (user) => <RelativeTime date={user.created_at} />,
  },
];

export const createUserActions = (
  onAction: ActionHandler<User, UserAction>,
  isLoading: boolean,
  options?: {
    onImpersonate?: (user: User) => void;
    isImpersonating?: boolean;
  }
): TableAction<User>[] => [
  // Main actions
  {
    key: "view",
    label: "View Public Page",
    icon: Eye,
    onClick: (entity) => {
      if (entity.slug) {
        window.open(
          getUserProfileUrl(entity.slug),
          'userProfile',
          'width=800,height=800,scrollbars=yes,resizable=yes'
        );
      }
    },
    disabled: isLoading,
            colorPalette: "blue",
    visible: (entity) => !!entity.slug, // Only show if user has a slug
  },
  {
    key: "edit",
    label: "Edit",
    icon: Edit,
    onClick: (entity) => onAction("edit", entity),
    disabled: isLoading,
  },
  {
    key: "impersonate",
    label: "Impersonate",
    icon: UserCheck,
    onClick: (entity) => options?.onImpersonate?.(entity),
    disabled: isLoading || !!options?.isImpersonating,
    colorPalette: "orange",
    visible: () => typeof options?.onImpersonate === 'function',
  },
  // Role-based actions
  {
    key: "clone_profile",
    label: "Clone Profile",
    icon: Copy,
    onClick: (entity) => onAction("clone_profile", entity),
    disabled: isLoading,
            colorPalette: "green",
    visible: (entity) => entity.roles?.includes('doctor') || false,
  },
  {
    key: "copy_conditions",
    label: "Copy conditions",
    icon: ClipboardCopy,
    onClick: (entity) => onAction("copy_conditions", entity),
    disabled: isLoading,
    colorPalette: "green",
    visible: (entity) => entity.roles?.includes('doctor') || false,
  },
  {
    key: "manage_doctors",
    label: "Manage Doctors",
    icon: Settings,
    onClick: (entity) => onAction("manage_doctors", entity),
    disabled: isLoading,
            colorPalette: "brand",
    visible: (entity) => entity.roles?.includes('assistant') || false,
  },
  // Additional actions
  {
    key: "change_email",
    label: "Change Email",
    icon: Mail,
    onClick: (entity) => onAction("change_email", entity),
    disabled: isLoading,
            colorPalette: "blue",
  },
  {
    key: "forgot_password",
    label: "Send Password Reset",
    icon: KeyRound,
    onClick: (entity) => onAction("forgot_password", entity),
    disabled: isLoading,
            colorPalette: "orange",
  },
  {
    key: "change_password",
    label: "Change Password",
    icon: Lock,
    onClick: (entity) => onAction("change_password", entity),
    disabled: isLoading,
            colorPalette: "purple",
  },
  {
    key: "change_roles",
    label: "Change Roles",
    icon: Users,
    onClick: (entity) => onAction("change_roles", entity),
    disabled: isLoading,
            colorPalette: "teal",
  },
  {
    key: "change_slug",
    label: "Change URL",
    icon: LinkIcon,
    onClick: (entity) => onAction("change_slug", entity),
    disabled: isLoading,
    colorPalette: "blue",
    visible: (entity) => !!entity.profile?.id && !entity.roles?.includes('assistant'),
  },
  {
    key: "edit_configuration",
    label: "Change Settings",
    icon: Sliders,
    onClick: (entity) => onAction("edit_configuration", entity),
    disabled: isLoading,
    colorPalette: "cyan",
    visible: (entity) => !entity.roles?.includes('assistant'),
  },
]; 
